
import { useState } from "react";
import { DeliveryAddressSelector } from "./DeliveryAddressSelector";
import { TermsAcknowledgment } from "./TermsAcknowledgment";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ProposalAcceptanceSectionProps {
  organizationId: string;
  onAcceptanceReady: (ready: boolean, deliveryAddressId: string | null, orderReference: string | null, missing?: string[]) => void;
  isPilot?: boolean;
  className?: string;
}

export const ProposalAcceptanceSection = ({
  organizationId,
  onAcceptanceReady,
  isPilot = false,
  className = ""
}: ProposalAcceptanceSectionProps) => {
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<string | null>(null);
  const [termsAcknowledged, setTermsAcknowledged] = useState(false);
  const [orderReference, setOrderReference] = useState("");

  // Update parent component when any condition changes
  const updateAcceptanceStatus = (addressId: string | null, termsStatus: boolean, orderRef: string) => {
    const missing: string[] = [];
    if (!addressId) missing.push("Delivery address");
    if (!orderRef.trim()) missing.push("Order Reference Number");
    if (!termsStatus) missing.push("Terms & Conditions");
    const isReady = missing.length === 0;
    onAcceptanceReady(isReady, addressId, orderRef.trim() || null, missing);
  };

  const handleAddressSelect = (addressId: string | null) => {
    setSelectedDeliveryAddressId(addressId);
    updateAcceptanceStatus(addressId, termsAcknowledged, orderReference);
  };

  const handleTermsChange = (acknowledged: boolean) => {
    setTermsAcknowledged(acknowledged);
    updateAcceptanceStatus(selectedDeliveryAddressId, acknowledged, orderReference);
  };

  const handleOrderReferenceChange = (value: string) => {
    setOrderReference(value);
    updateAcceptanceStatus(selectedDeliveryAddressId, termsAcknowledged, value);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Delivery Address */}
      <DeliveryAddressSelector
        organizationId={organizationId}
        selectedAddressId={selectedDeliveryAddressId}
        onAddressSelect={handleAddressSelect}
      />

      {/* Order Reference */}
      <div className="space-y-2">
        <h3 className="text-left text-lg font-semibold">
          Order Reference Number *
        </h3>
        <Input
          id="order-reference"
          type="text"
          placeholder="Enter order reference"
          value={orderReference}
          onChange={(e) => handleOrderReferenceChange(e.target.value)}
          className="w-full bg-slate-50 border-slate-200"
          required
        />
        <p className="text-xs text-muted-foreground">
          Please provide your purchase order number or internal reference for this order.
        </p>
      </div>

      {/* Terms & Conditions */}
      <TermsAcknowledgment
        onAcknowledgmentChange={handleTermsChange}
        variant={isPilot ? "pilot" : "standard"}
      />
    </div>
  );
};
